import { signal } from "@preact/signals";

/**
 * Where an install hint is being shown. The two are different messages, not the same
 * one twice: home makes the general case, and create warns that a game made in a
 * browser doesn't follow you into an app you add to your home screen afterwards. So
 * they get their own dismissals — "not now" to the general pitch, made once, must not
 * be able to silence the one that arrives at the moment it matters.
 */
export type HintScope = "home" | "create";

/** The home key keeps its original name so dismissals already in the wild still count. */
const DISMISSED_KEYS: Record<HintScope, string> = {
  home: "golf:install-dismissed",
  create: "golf:install-dismissed-create",
};

let deferredPrompt: any = null;

export const canInstall = signal(false);
export const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !(navigator as any).standalone;

function alreadyInstalled(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true
  );
}

/**
 * Dismissal has to drive a signal, not just a localStorage read. On iOS `canInstall` is
 * already false (there's no beforeinstallprompt), so nothing re-rendered on dismiss and
 * "Got it" appeared to do nothing until the next reload. Bumping a version counter is
 * the same trick `identity.ts` uses for the organiser's mode.
 */
const dismissals = signal(0);

function isDismissed(scope: HintScope): boolean {
  dismissals.value;
  try {
    return localStorage.getItem(DISMISSED_KEYS[scope]) === "1";
  } catch {
    return false;
  }
}

export function dismissInstallPrompt(scope: HintScope = "home") {
  try {
    localStorage.setItem(DISMISSED_KEYS[scope], "1");
  } catch {
    // storage unavailable — dismissing for this session is still better than nothing
  }
  dismissals.value++;
  // Deliberately does *not* clear `canInstall`. That used to be how a dismissal took
  // effect, before the signal above existed, and it threw away the deferred prompt for
  // the whole session — so with two places to dismiss, turning down either one would
  // have silently disabled the other.
}

export function shouldShowInstallHint(scope: HintScope = "home"): boolean {
  if (alreadyInstalled() || isDismissed(scope)) return false;
  return canInstall.value || isIOS;
}

export async function promptInstall(): Promise<void> {
  if (!deferredPrompt) return;
  await deferredPrompt.prompt();
  deferredPrompt = null;
  canInstall.value = false;
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredPrompt = event;
  canInstall.value = true;
});

window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  canInstall.value = false;
});
